import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseUrl = process.env.REACT_APP_BASE_URL;

export const handle401Error = async () => {
  console.log("handle401Error called!");
  // Rest of your middleware logic
};

export const orderApi = createApi({
  reducerPath: "orderApi",
  baseQuery: fetchBaseQuery({
    baseUrl,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  }),
  tagTypes: ["Orders"],
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(handle401Error),
  endpoints: (builder) => ({
    createStripeCheckoutSession: builder.mutation({
      query: (payload) => ({
        url: "payments/checkout/session",
        method: "POST",
        body: payload,
      }),
    }),
    confirmStripeCheckoutSession: builder.mutation({
      query: (payload) => ({
        url: "payments/checkout/confirm",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Orders"],
    }),
    createOrder: builder.mutation({
      query: (payload) => ({
        url: "orders/create",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Orders"],
    }),
    getOrder: builder.query({
      query: (params = {}) => {
        // Back-compat: useGetOrderQuery(false) / useGetOrderQuery(true)
        if (typeof params === "boolean") {
          return params ? `orders?supplier=true` : `orders`;
        }
        const {
          page = 1,
          limit = 10,
          sortBy = "order.created",
          sortOrder = "DESC",
          searchFilter,
          startDate,
          endDate,
          status,
          marketplace,
          supplier = false,
        } = params || {};
        const queryParams = {
          page,
          limit,
          sortBy,
          sortOrder,
        };
        // Only send supplier=true — NestJS receives query params as strings,
        // so supplier=false is truthy and wrongly scopes to supplier-owned orders.
        if (supplier === true || supplier === "true") {
          queryParams.supplier = true;
        }
        if (searchFilter) queryParams.searchFilter = searchFilter;
        if (startDate) queryParams.startDate = startDate;
        if (endDate) queryParams.endDate = endDate;
        if (status) queryParams.status = status;
        if (marketplace) queryParams.marketplace = marketplace;
        return {
          url: "orders",
          method: "GET",
          params: queryParams,
        };
      },
      providesTags: ["Orders"],
    }),
    getOrderById: builder.query({
      query: (orderId) => `orders/${orderId}`,
      providesTags: (_result, _error, orderId) => [{ type: "Orders", id: orderId }],
    }),
    getSpendingSummary: builder.query({
      query: (period = "30") => `orders/spending-summary?period=${period}`,
      providesTags: ["Orders"],
    }),
    updateOrder: builder.mutation({
      query: ({ orderId, status, reason }) => ({
        url: `orders/${orderId}`,
        method: "PUT",
        body: { status, ...(reason && { reason }) },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        "Orders",
        { type: "Orders", id: orderId },
      ],
    }),
    deleteOrder: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Orders"],
    }),
  }),
});

export const {
  useCreateStripeCheckoutSessionMutation,
  useConfirmStripeCheckoutSessionMutation,
  useCreateOrderMutation,
  useGetOrderQuery,
  useGetOrderByIdQuery,
  useGetSpendingSummaryQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
} = orderApi;
